import { call } from "./use"

/**
 * Mixin for accessing other controllers on the page using "outlets".
 *
 * To use, add the outlet values and useOutlets call in the component's connect
 * function:
 *
 *   connect() {
 *     useOutlets(this, {
 *       components: ["ol1"],
 *       scope: this.element // For local scope
 *     })
 *   }
 *
 * Specify the "components" array to define the child components that attributes
 * available for the outlet. For each component, you must define the
 * `data-#{component_name}-outlet-key` and `data-#{component_name}-outlet-selector`
 * attributes on the controller element.
 *
 * We have helper functions for building out the attributes & values in
 * `ApplicationComponent`. For example, if you wanted to create an outlet
 * connection with a vis child component:
 *
 *   class QueryComponent < ApplicationComponent
 *     def extra_properties
 *       {
 *         outlet_data_key("ol1") => controller_name(Query::Ol1Component),
 *         outlet_selector_key("ol1") => outlet_class(Query::Ol1Component)
 *       }.merge(outlet_hash("ol2", Query::Ol2Component)) # even easier
 *     end
 *   end
 *
 * You must also include `useOutlets(this)` in the child outlet. There is no
 * need to set a outlet-data-key or outlet-selector in this component unless you
 * want to reference another outlets in that component.
 *
 * This also injects helper methods for accessing the outlet controller(s) and
 * provides an implementable function that is called when all outlets have been
 * connected (even nested components).
 *
 *   outletsConnected() {
 *     // All outlets components have connected
 *     this.outletComponent("ol1").componentFunction()
 *   }
 *
 * All outlets are optionally loaded and return null if not found.
 *
 */
export const useOutlets = (controller, options) => {
  const {element, application} = controller;
  const scope = options?.scope ?? document;

  let _outletConnected = false;

  controller.__scopedOutlets ??= {}
  options?.components?.forEach(e => controller.__scopedOutlets[e] = scope)

  function getSelector(name) {
    return element.getAttribute(`data-${name}-outlet-selector`)
  }

  function getKey(name) {
    return element.getAttribute(`data-${name}-outlet-key`)
  }

  function outletElements(name = null) {
    if (name === null) {
      return Object.entries(controller.__scopedOutlets).flatMap(([name, scope]) => {
        return Array.from(scope.querySelectorAll(getSelector(name)))
      })
    } else {
      return controller.__scopedOutlets[name].querySelectorAll(getSelector(name))
    }
  }

  function outletElement(name) {
    return controller.__scopedOutlets[name].querySelector(getSelector(name))
  }

  function outletComponents(name = null) {
    if (name === null) {
      return Object.entries(controller.__scopedOutlets).flatMap(([name]) => {
        return Array.from(outletElements(name)).map(el =>
          application.getControllerForElementAndIdentifier(el, getKey(name))
        )
      })
    } else {
      return Array.from(outletElements(name)).map(el =>
        application.getControllerForElementAndIdentifier(el, getKey(name))
      )
    }
  }

  function outletComponent(name) {
    return application.getControllerForElementAndIdentifier(outletElement(name), getKey(name))
  }

  function hasOutlets(component) {
    return Object.keys(component.__scopedOutlets).length > 0
  }

  function hasOutletElements(component) {
    return component.__hasOutletElements
  }

  function outletConnected() {
    return _outletConnected;
  }

  controller.__hasOutletElements = outletElements().length > 0

  if(hasOutlets(controller)) {
    Object.assign(controller, {
      outletElements: (name) => outletElements(name),
      outletElement: (name) => outletElement(name),
      outletComponents: (name) => outletComponents(name),
      outletComponent: (name) => outletComponent(name),
      outletConnected: () => outletConnected()
    })

    const els = outletElements().filter(c => c !== null)
    const comps = outletComponents().filter(c => c !== null)

    let outletCt = els.length - comps.length

    let connected = () => {
      if((outletCt -= 1) === 0) {
        _outletConnected = true;
        call(controller, "outletsConnected")
      }
    }

    if (outletCt === 0) {
      _outletConnected = true;
      call(controller, "outletsConnected")
    }

    els.forEach(el => {
      el._stimulusConnected = function(component) {
        /**
         * If the child has outlets, then don't consider us connected
         * until all their outlets have been connected.
         */
        if(call(component, "outletConnected")) {
          connected()
        } else if(hasOutletElements(component)) {
          const _outletsConnected = component.outletsConnected?.bind(component);
          Object.assign(component, {
            outletsConnected() {
              connected()

              if(_outletsConnected) _outletsConnected()
            }
          })
        } else {
          connected()
        }
      }
    })
  }

  call(element, "_stimulusConnected", [controller])
}
